import {
    Breakpoints,
    MultiSelect,
    Text,
    TextTag,
    TokenTextAppearance,
    TokenTextColor,
} from '@volkswagen-onehub/components-core'
import styled from 'styled-components'
import {useTranslation} from 'react-i18next'
import React, {useEffect, useRef} from 'react'
import {useDispatch, useSelector} from 'react-redux'
import {AppDispatch} from '../thunk'
import {selectTracking, trackingData} from '../../shared/trackingServiceHelper'
import {RootState} from '../rootReducer'
import {getLabel, setUrlParams} from '../../shared/utilities'
import {useFeatureServices} from '../../use-feature-services'
import {VisuallyHidden} from '../../shared/styled'

const SelectContainer = styled.div`
    display: grid;
    grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
    align-items: center;
    grid-gap: ${(props) => props.theme.size.static200};
    margin-bottom: ${(props) => props.theme.size.static400};
    min-width: 0;

    &:last-of-type {
        margin-bottom: 0;
    }

    @media (min-width: ${Breakpoints.b560}px) {
        display: block;
        margin-bottom: ${(props) => props.theme.size.static400};
    }

    @media (min-width: ${Breakpoints.b1280}px) {
        margin: 0;
    }
`

const FilterLabel = styled(Text)`
    padding-bottom: ${(props) => props.theme.size.static200};
`

const StyledMultiSelect = styled(MultiSelect)`
    && {
        box-sizing: border-box;
        height: auto;
        max-height: 76px;
        max-width: 100%;
        min-height: 50px;
        min-width: 0;
        width: 100%;
    }

    && > div[role='combobox'] {
        align-items: center;
        box-sizing: border-box;
        column-gap: ${(props) => props.theme.size.static100};
        display: grid;
        grid-template-columns: minmax(0, 1fr) auto;
        min-width: 0;
        width: 100%;
    }

    && > div[role='combobox'] > div:first-child {
        align-items: center;
        column-gap: ${(props) => props.theme.size.static100};
        display: grid;
        grid-template-columns: minmax(0, 1fr) auto;
        min-width: 0;
        overflow: hidden;
    }

    && > div[role='combobox'] > div:first-child > span:first-child {
        min-width: 0;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
    }

    && > div[role='combobox'] > div:first-child > span:last-child {
        justify-self: start;
    }

    && > div[role='combobox'] > span:last-child {
        justify-self: end;
    }

    &[disabled] div[class*='StyledInteractionWrapper'] {
        color: ${(props) => props.theme.colors.content.secondary};
    }
`

interface SelectProps {
    title: string
    options: any
    setElement: any
    selectKey: string
    defaultValue: string[]
}

export function FilterSelect(props: SelectProps): React.JSX.Element {
    const {t} = useTranslation('fa-flotte')
    const {'s2:history': historyService} = useFeatureServices() as any
    const dispatch = useDispatch() as AppDispatch

    const reduxValue = useSelector(
        (state: RootState) => state.filterValues[props.selectKey]
    )

    // Normalize the redux value to an array of option-key strings. Some
    // slices store the value as numbers (e.g. totKm, period) while the
    // MultiSelect option keys are always strings, so we cast back to
    // string here to compare/select consistently.
    const selectedOptionKeys: string[] = Array.isArray(reduxValue)
        ? reduxValue.map((v) => String(v))
        : reduxValue
        ? [String(reduxValue)]
        : []

    const selectedCount = selectedOptionKeys.length
    const isSingleOption = Object.keys(props.options).length === 1
    const selectContainerRef = useRef<HTMLDivElement>(null)
    const filterTitleId = `filter-${props.selectKey}-title`
    const multiSelectLabelId = `filter-${props.selectKey}-multiselect-label`
    const listboxId = `filter-${props.selectKey}-listbox`

    const dispatchSelection = (next: string[]) => {
        dispatch(props.setElement(next))
    }

    const onOptionClick = (optionKey: string) => {
        // Toggle the option within the currently selected set kept in
        // redux. We drive the MultiSelect in controlled mode (via the
        // `value` prop) so the visible selection counter always reflects
        // the redux state - including when the values come from URL
        // params parsed on mount, from the single-option auto-select
        // effect below, or after the user navigates back from the dealer
        // form.
        const next = selectedOptionKeys.includes(optionKey)
            ? selectedOptionKeys.filter((key) => key !== optionKey)
            : [...selectedOptionKeys, optionKey]

        dispatchSelection(next)

        selectTracking(props.title, trackingData.MULTI_VALUE, [next])
    }

    useEffect(() => {
        setUrlParams(historyService, getLabel(props.title), reduxValue)
    }, [reduxValue])

    useEffect(() => {
        if (isSingleOption) {
            dispatch(props.setElement(Object.keys(props.options)))
        }
    }, [props.options])

    // components-core doesn't expose props for the listbox and its options, so we
    // patch the DOM after every render (options and selection can change):
    // - the listbox gets an accessible name pointing to the visible filter title
    // - the checkboxes it renders get the option role, the only one (with group)
    //   allowed inside a listbox
    useEffect(() => {
        const listbox =
            selectContainerRef.current?.querySelector('[role="listbox"]')

        if (!listbox) {
            return
        }

        listbox.setAttribute('aria-labelledby', filterTitleId)

        listbox
            .querySelectorAll<HTMLInputElement>('input[type="checkbox"]')
            .forEach((checkbox) => {
                checkbox.setAttribute('role', 'option')
                checkbox.setAttribute('aria-selected', String(checkbox.checked))
            })
    })

    return (
        <SelectContainer ref={selectContainerRef}>
            <FilterLabel
                id={filterTitleId}
                tag={TextTag.p}
                appearance={TokenTextAppearance.headline200}
                color={TokenTextColor.primary}
                bold
            >
                {props.title}
                {selectedCount > 0 && (
                    <VisuallyHidden>
                        {` (${selectedCount} ${
                            selectedCount === 1
                                ? 'filtro selezionato'
                                : 'filtri selezionati'
                        })`}
                    </VisuallyHidden>
                )}
            </FilterLabel>

            <StyledMultiSelect
                key={props.selectKey}
                name={props.title}
                label={
                    isSingleOption
                        ? props.options[Object.keys(props.options)[0]]
                        : t('filters.labels.select')
                }
                aria-label={
                    isSingleOption
                        ? props.options[Object.keys(props.options)[0]]
                        : t('filters.aria-labels.select', {
                              filter: t(
                                  `filters.aria-labels.${props.selectKey}`
                              ),
                          })
                }
                options={props.options}
                defaultSingleOption={
                    isSingleOption
                        ? props.options[Object.keys(props.options)[0]]
                        : undefined
                }
                controlsId={listboxId}
                labelId={multiSelectLabelId}
                disabled={isSingleOption}
                value={selectedOptionKeys}
                onOptionClick={onOptionClick}
            />
        </SelectContainer>
    )
}
